import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconTruck } from "../components/icons";
import { KpiTile, SparkLine } from "../components/intelligence/KpiTile";
import type { TransportCteDashboardDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

export function OperationalPage() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data: cte, isLoading, isError: cteError, refetch: cteRefetch } = useQuery({
    queryKey: ["transport", "cte", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCteDashboardDto>(
        `/dashboards/transport/cte?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = cte?.summary;
  const p = cte?.prevSummary;

  // ── Deltas ────────────────────────────────────────────────────────────────
  const receitaDelta   = computeDelta(s?.totalValue,      p?.totalValue,      { direction: "higher_is_better" });
  const countDelta     = computeDelta(s?.count,           p?.count,           { direction: "higher_is_better" });
  const pedagioDelta   = computeDelta(s?.tollValue,       p?.tollValue,       { direction: "higher_is_worse"  });
  // ICMS real do período anterior. Antes era estimado pela proporção ICMS/receita
  // de AGORA aplicada à receita anterior — o que tornava esta variação sempre
  // idêntica à da receita.
  const icmsDelta      = computeDelta(s?.totalIcms,       p?.totalIcms,       { direction: "neutral" });
  const mercDelta      = computeDelta(s?.goodsValue,      p?.goodsValue,      { direction: "higher_is_better" });
  const quantidadeDelta = computeDelta(s?.totalQuantity,  p?.totalQuantity,   { direction: "higher_is_better" });
  const pesoDelta       = computeDelta(s?.totalWeightKg,  p?.totalWeightKg,   { direction: "higher_is_better" });

  const byDay       = cte?.byDay       ?? [];
  const ticketTrend = cte?.ticketTrend ?? [];
  const topCustomers = cte?.topCustomers ?? [];
  const topVehicles  = cte?.topVehicles  ?? [];
  const customerList = useProgressiveList(topCustomers);
  const vehicleList  = useProgressiveList(topVehicles);

  // Mini-gráficos dos cards: só Receita e CT-es têm série diária (byDay) —
  // os demais (mercadoria, ICMS, pedágio, quantidade, peso) não têm quebra por
  // dia no DTO, então não ganham sparkline (não inventar visual sem dado real).
  const dailyRevenue = byDay.map((d) => d.totalValue);
  const dailyCount   = byDay.map((d) => d.count);
  const maxVehicleRevenue = Math.max(...topVehicles.map((v) => v.totalValue), 1);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operacional CT-e"
        subtitle="Emissão, clientes, veículos e análise do faturamento por frete"
      />

      {cteError && <QueryErrorBanner onRetry={() => { cteRefetch(); }} />}

      {/* KPIs — mesmo padrão visual do Financeiro/Executiva (KpiTile + sparkline onde há série diária) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Receita CT-e"
          value={s ? fmtBRL(s.totalValue) : "—"}
          delta={receitaDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-receita-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          visual={<SparkLine values={dailyRevenue} label="Receita CT-e por dia no período" />}
          footnote="Receita por dia. O ponto marca o dia mais recente"
          loading={isLoading}
        />
        <KpiTile
          label="CT-es emitidos"
          value={s ? fmtNumber(s.count) : "—"}
          delta={countDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-count-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          visual={<SparkLine values={dailyCount} label="CT-es emitidos por dia no período" />}
          footnote="CT-es autorizados por dia. O ponto marca o dia mais recente"
          loading={isLoading}
        />
        <KpiTile
          label="Mercadoria transportada"
          value={s ? fmtBRL(s.goodsValue) : "—"}
          delta={mercDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-mercadoria-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          loading={isLoading}
        />
        <KpiTile
          label="ICMS"
          value={s ? fmtBRL(s.totalIcms) : "—"}
          delta={icmsDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-icms-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          loading={isLoading}
        />
        <KpiTile
          label="Pedágios"
          value={s ? fmtBRL(s.tollValue) : "—"}
          delta={pedagioDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          status={s && s.tollValue > 0 ? "warning" : null}
          memory={{ cardKey: "cte-pedagio-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          loading={isLoading}
        />
        <KpiTile
          label="Qtde transportada"
          value={s ? fmtNumber(Math.round(s.totalQuantity)) : "—"}
          delta={quantidadeDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-quantidade-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          loading={isLoading}
        />
        <KpiTile
          label="Peso total (Kg)"
          value={s ? fmtNumber(Math.round(s.totalWeightKg)) : "—"}
          delta={pesoDelta}
          deltaLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
          memory={{ cardKey: "cte-peso-mes", periodFrom: from, periodTo: to, area: "operacional" }}
          loading={isLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="CT-es por dia" subtitle="Volume no mês atual" height={240}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-cte" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDateShort}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v: number, n: string) => [
                  n === "count" ? fmtNumber(v) : fmtBRL(v),
                  n === "count" ? "CT-es" : "Valor",
                ]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="var(--brand)"
                strokeWidth={2}
                fill="url(#grad-cte)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Evolução do Ticket Médio"
          subtitle="Por mês, nos últimos 5 meses"
          height={240}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ticketTrend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-ticket" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => `R$ ${(v / 1000).toFixed(1)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: number) => [fmtBRL(v), "Ticket médio"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Bar dataKey="avgTicket" fill="url(#grad-ticket)" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top clients + vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clients */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Top Clientes por Faturamento</p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topCustomers.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : customerList.visible.map((c, i) => {
              const sharePct = s && s.totalValue > 0 ? (c.totalValue / s.totalValue) * 100 : 0;
              return (
                <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                  title="Ver CT-es que compõem o faturamento (memória de cálculo)"
                  onClick={() => navigate(`/operacional/memoria/cte-receita-mes?periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}`)}>
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                    <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-tm-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max(sharePct, 2), 100)}%` }} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-tm-700">{c.formatted}</p>
                    <p className="text-xs text-gray-400">{sharePct.toFixed(1)}% · {fmtNumber(c.cteCount)} CT-es</p>
                  </div>
                </div>
              );
            })}
            {!isLoading && <LoadMoreBar list={customerList} label="clientes" />}
          </div>
        </div>

        {/* Top vehicles */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              Receita por Veículo
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : vehicleList.visible.map((v, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Abrir o raio-X da placa"
                onClick={() => navigate(`/frota/placa/${encodeURIComponent(v.plate)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-tm-700">{v.plate}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-tm-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max((v.totalValue / maxVehicleRevenue) * 100, 2), 100)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{v.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {[v.brand, v.modelYear, `${fmtNumber(v.cteCount)} CT-es`].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={vehicleList} label="veículos" />}
          </div>
        </div>
      </div>
    </div>
  );
}
